import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosConfig';
import { Button, Table, message, Input } from 'antd';
import { MinusOutlined, PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import Header from '../../../components/Header/Header';
import Footer from '../../../components/Footer/Footer';
import AddIndentModal from './AddIndentModal';
import ExportPopupDB from './DetailedBatch/ExportPopupDB';

const BatchDetail = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddIndentModalVisible, setIsAddIndentModalVisible] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false); // Add this state

  useEffect(() => {
    fetchBatchDetails();
  }, [batchId]);

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/batch-indents/${batchId}`);
      console.log('Batch details response:', response.data);
      setBatch(response.data);
    } catch (error) {
      console.error('Error fetching batch details:', error);
      message.error('Failed to fetch batch details');
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveIndent = async (indentId) => {
    try {
      await axiosInstance.delete(`/batch-indents/${batchId}/indents/${indentId}`);
      message.success('Indent removed from batch successfully');
      fetchBatchDetails();
    } catch (error) {
      console.error('Error removing indent:', error);
      message.error('Failed to remove indent from batch');
    }
  };

  const handleAddIndent = () => {
    setIsAddIndentModalVisible(true);
  };

  const handleAddIndentSuccess = () => {
    setIsAddIndentModalVisible(false);
    fetchBatchDetails();
  };

  // Add this function to handle export
  const handleExport = () => {
    setShowExportPopup(true);
  };

  const columns = [
    {
      title: 'Indent ID',
      dataIndex: 'IndentID',
      key: 'IndentID',
    },
    {
      title: 'Project',
      dataIndex: 'ProjectName',
      key: 'ProjectName',
    },
    {
      title: 'Stage',
      dataIndex: 'Status',
      key: 'Status',
      render: (status) => status || 'Pending'
    },
    {
      title: 'Created At',
      dataIndex: 'CreationDate',
      key: 'CreationDate',
      render: (date) => {
        if (!date) return 'N/A';
        try {
          return new Date(date).toLocaleDateString();
        } catch (error) {
          console.error('Error formatting date:', error);
          return 'Invalid Date';
        }
      }
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          danger
          icon={<MinusOutlined />}
          onClick={() => handleRemoveIndent(record.IndentID)}
        >
          Remove
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  if (!batch) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <div className="flex-grow p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Batch Details</h1>
              <p className="text-gray-600">Batch Number: {batch.BatchNumber}</p>
              <p className="text-gray-600">Description: {batch.Description}</p>
            </div>
            <div className="flex space-x-2">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                className="flex items-center"
              >
                Export
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddIndent}
              >
                Add Indent
              </Button>
              <Button onClick={() => navigate('/batch-indent')}>
                Back to Batches
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Indents in Batch</h2>
            {batch.Indents && batch.Indents.length > 0 ? (
              <Table
                columns={columns}
                dataSource={batch.Indents}
                rowKey="IndentID"
                pagination={false}
              />
            ) : (
              <p className="text-gray-500 italic">No indents in this batch</p>
            )}
          </div>
        </div>
      </div>

      <Footer />

      <AddIndentModal
        open={isAddIndentModalVisible}
        onClose={() => setIsAddIndentModalVisible(false)}
        onAdd={handleAddIndentSuccess}
        batchId={batchId}
      />

      {/* Export Popup */}
      {showExportPopup && (
        <ExportPopupDB
          open={showExportPopup}
          onCancel={() => setShowExportPopup(false)}
          batch={batch}
          items={batch.Indents}
        />
      )}
    </div>
  );
};

export default BatchDetail;